var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { DataType, MetaData, utils as dataUtils } from '@easydata/core';
import { i18n } from '@easydata/core';
import { AggrFunction } from './aggr_function';
import { Operator, Operand } from './operator';
import { Entity, EntityAttr } from './entity';
import { EqValueEditor } from './value_editor';
import { DataKind } from '../types/data_kind';
import { EqEditorTag } from '../types/eq_editor_tag';
import { Query } from '../query/query';
import { ExprTag } from '../types/expr_tag';
import { Tree } from '../common/tree';
/**
 * Represents a data model
 */
var DataModel = /** @class */ (function (_super) {
    __extends(DataModel, _super);
    /** The default constructor. */
    function DataModel() {
        var _this = _super.call(this) || this;
        _this.mainEntity = null;
        _this.nullAttribute = new EntityAttr(null);
        _this.nullOperator = new Operator();
        _this.operators = new Array();
        _this.rootEntity = new Entity();
        _this.aggrFunctions = new Array();
        _this.dateMacros = ['Today', 'Yesterday',
            'Tomorrow', 'FirstDayOfMonth', 'LastDayOfMonth',
            'FirstDayOfWeek', 'FirstDayOfYear', 'FirstDayOfNextWeek', 'FirstDayOfPrevMonth',
            'FirstDayOfNextMonth', 'FirstDayOfNextYear', 'FirstDayOfPrevYear', 'FirstDayOfPrevWeek'];
        _this.timeMacros = ['Now', 'HourStart', 'Midnight', 'Noon'];
        _this.links = [];
        return _this;
    }
    /**
     * Gets the main entity of model
     * @return The main entity.
     */
    DataModel.prototype.getMainEntity = function () {
        return this.mainEntity;
    };
    /**
     * Loads data model from JSON.
     * @param stringJson The JSON string.
     */
    DataModel.prototype.loadFromJSON = function (stringJson) {
        var model = JSON.parse(stringJson);
        this.loadFromData(model);
    };
    /**
     * Loads data model from its JSON representation object.
     * @param data The JSON representation object.
     */
    DataModel.prototype.loadFromData = function (data) {
        _super.prototype.loadFromData.call(this, data);
        //Operators
        this.operators = new Array();
        if (data.operators) {
            for (var i = 0; i < data.operators.length; i++) {
                var newOperator = new Operator();
                newOperator.loadFromData(this, data.operators[i]);
                this.operators.push(newOperator);
            }
        }
        //rootEntity
        this.rootEntity.loadFromData(this, data.entroot);
        //Aggr Functions
        this.aggrFunctions = new Array();
        if (data.aggrfuncs) {
            for (var i = 0; i < data.aggrfuncs.length; i++) {
                var newAggrFunc = new AggrFunction();
                newAggrFunc.loadFromData(data.aggrfuncs[i]);
                this.aggrFunctions.push(newAggrFunc);
            }
        }
    };
    /**
     * Gets the data model object.
     * @returns Tha data model.
     */
    DataModel.prototype.getObject = function () {
        return this;
    };
    /**
     * Sets data to data model.
     * @param model Its JSON representation object or JSON string.
     */
    DataModel.prototype.setData = function (model) {
        if (typeof model === 'string') {
            this.loadFromJSON(model);
        }
        else {
            this.loadFromData(model);
        }
    };
    /**
     * Creates entity.
     * @param parent The parent entity.
     * @returns The Entity.
     */
    DataModel.prototype.createEntity = function (parent) {
        return new Entity(parent);
    };
    /**
     * Creates entity attribute.
     * @param parent The parent entity.
     * @returns The entity attribute.
     */
    DataModel.prototype.createEntityAttr = function (parent) {
        return new EntityAttr(parent);
    };
    /**
     * Creates new EqValueEditor
     * @returns An instance of EqValueEditor class
     */
    DataModel.prototype.createValueEditor = function () {
        return new EqValueEditor();
    };
    /**
     * Creates query.
     * @returns The Query.
     */
    DataModel.prototype.createQuery = function () {
        return new Query(this);
    };
    /**
     * Gets root entity of the data model.
     * @returns The root entity.
     */
    DataModel.prototype.getRootEntity = function () {
        return this.rootEntity;
    };
    /**
     * Finds editor by its ID.
     * @param editorId The editor ID.
     * @returns The value editor or `null`.
     */
    DataModel.prototype.getEditorById = function (editorId) {
        return _super.prototype.getEditorById.call(this, editorId);
    };
    /**
     * Gets entity attribute by its ID.
     * This function runs through all attributes inside specified model (it's root entity and all its sub-entities).
     * @param attrId The attribute ID.
     * @returns The attribute or `null`.
     */
    DataModel.prototype.getAttributeById = function (attrId) {
        var attr = this.getEntityAttrById(this.getRootEntity(), attrId);
        if (!attr) {
            return null;
        }
        return attr;
    };
    /**
    * Gets entity attribute by its ID.
    * This function runs through all attributes inside specified model (it's root entity and all its sub-entities).
    * @param attrId The attribute ID.
    * @returns The attribute or `nullAttribute`.
    */
    DataModel.prototype.getAttributeByIdSafe = function (attrId) {
        var attr = this.getAttributeById(attrId);
        if (!attr)
            return this.nullAttribute;
        return attr;
    };
    /**
     * Checks wether attribute contains such property.
     * @param attrId The attribute ID.
     * @param propName The property name.
     * @returns `true` if the attribute contains the property, otherwise `false`.
     */
    DataModel.prototype.checkAttrProperty = function (attrId, propName) {
        var attribute = this.getAttributeById(attrId);
        if (attribute) {
            if (typeof attribute[propName] === 'undefined') {
                throw 'No such property: ' + propName;
            }
            if (attribute[propName]) {
                return true;
            }
            else if (attribute.lookupAttr) {
                attrId = attribute.lookupAttr;
                attribute = this.getAttributeById(attrId);
                return attribute && attribute[propName];
            }
            else {
                return false;
            }
        }
        else
            return false;
    };
    /**
     * Gets entity attribute by its ID.
     * This function runs through all attributes inside specified entity and all its sub-entities.
     * @param entity
     * @param attrId
     * @returns The attribute or `null`.
     */
    DataModel.prototype.getEntityAttrById = function (entity, attrId) {
        return _super.prototype.getEntityAttrById.call(this, entity, attrId);
    };
    DataModel.prototype._listByEntityWithFilter = function (entity, filterFunc) {
        var result = new Array();
        var caption;
        var ent = null;
        if (entity.subEntities) {
            var subEntityCount = entity.subEntities.length;
            for (var entIdx = 0; entIdx < subEntityCount; entIdx++) {
                ent = entity.subEntities[entIdx];
                if (!filterFunc || filterFunc(ent, null)) {
                    caption = i18n.getText('Entities', ent.name);
                    if (!caption) {
                        caption = ent.caption;
                    }
                    var newEnt = dataUtils.assign(new Entity(), { id: ent.name, text: caption, items: [], isEntity: true });
                    newEnt.items = this._listByEntityWithFilter(ent, filterFunc);
                    if (newEnt.items.length > 0)
                        result.push(newEnt);
                }
            }
        }
        var attr = null;
        if (entity.attributes) {
            var attrCount = entity.attributes.length;
            for (var attrIdx = 0; attrIdx < attrCount; attrIdx++) {
                attr = entity.attributes[attrIdx];
                if (!filterFunc || filterFunc(entity, attr)) {
                    caption = i18n.getText('Attributes', attr.id);
                    if (!caption)
                        caption = attr.caption;
                    var newEnt = dataUtils.assign(new Entity(), { id: attr.id, text: caption, dataType: attr.dataType });
                    result.push(newEnt);
                }
            }
        }
        return result;
    };
    DataModel.prototype._listByEntity = function (entity, opts, filterFunc) {
        opts = opts || {};
        var resultEntities = [];
        var resultAttributes = [];
        var caption;
        var ent = null;
        if (entity.subEntities) {
            var subEntityCount = entity.subEntities.length;
            for (var entIdx = 0; entIdx < subEntityCount; entIdx++) {
                ent = entity.subEntities[entIdx];
                if (!filterFunc || filterFunc(ent, null)) {
                    if (opts.addUIC !== false && ent.useInConditions !== false ||
                        opts.addUIR !== false && ent.useInResult !== false ||
                        opts.addUIS !== false && ent.useInSorting !== false) {
                        caption = i18n.getText('Entities', ent.name) || ent.caption;
                        var newEnt = dataUtils.assign(new Entity(), {
                            id: ent.name,
                            text: caption,
                            items: [],
                            isEntity: true,
                            description: ent.description
                        });
                        var newOpts = dataUtils.assign({}, opts);
                        newOpts.includeRootData = false;
                        newEnt.items = this._listByEntity(ent, newOpts, filterFunc);
                        if (newEnt.items.length > 0) {
                            resultEntities.push(newEnt);
                        }
                    }
                }
            }
        }
        var attr = null;
        if (entity.attributes) {
            var attrCount = entity.attributes.length;
            for (var attrIdx = 0; attrIdx < attrCount; attrIdx++) {
                attr = entity.attributes[attrIdx];
                if (!filterFunc || filterFunc(entity, attr)) {
                    if (opts.addUIC !== false && attr.useInConditions !== false ||
                        opts.addUIR !== false && attr.useInResult !== false ||
                        opts.addUIS !== false && attr.useInSorting !== false) {
                        caption = i18n.getText('Attributes', attr.id) || attr.caption;
                        resultAttributes.push(dataUtils.assign(new EntityAttr(entity), { id: attr.id, text: caption, dataType: attr.dataType, lookupAttr: attr.lookupAttr, description: attr.description }));
                    }
                }
            }
        }
        var sortCheck = function (a, b) {
            if (a.text.toLowerCase() == b.text.toLowerCase()) {
                return 0;
            }
            if (a.text.toLowerCase() > b.text.toLowerCase()) {
                return 1;
            }
            return -1;
        };
        if (opts.sortEntities) {
            resultEntities.sort(sortCheck);
            resultAttributes.sort(sortCheck);
        }
        var result;
        if (!opts.attrPlacement || opts.attrPlacement == 0) {
            result = resultEntities.concat(resultAttributes);
        }
        else {
            result = resultAttributes.concat(resultEntities);
        }
        if (opts.attrPlacement == 2) {
            result.sort(sortCheck);
        }
        if (opts.includeRootData) {
            caption = i18n.getText('Entities', entity.name);
            if (!caption)
                caption = entity.caption;
            return { id: entity.name, text: caption, items: result };
        }
        else {
            return result;
        }
    };
    /**
     * Clears data model.
     */
    DataModel.prototype.clear = function () {
        _super.prototype.clear.call(this);
        this.operators = [];
        this.aggrFunctions = [];
    };
    /**
     * Add or update an operator.
     * @param desc The operator descriptor.
     * @returns The operator.
     */
    DataModel.prototype.addOrUpdateOperator = function (desc) {
        var op = dataUtils.findItemById(this.operators, desc.id);
        if (!op) {
            op = new Operator();
            op.id = desc.id;
            this.operators.push(op);
        }
        op.caption = desc.caption;
        op.expr = desc.expr || '';
        op.displayFormat = desc.format;
        op.defaultOperand = new Operand();
        op.defaultOperand.kind = desc.kind || DataKind.Scalar;
        if (desc.appliedTypes)
            op.appliedTypes = desc.appliedTypes;
        if (desc.paramCount > 0) {
            op.paramCount = desc.paramCount;
        }
        this.runThroughEntities(function (attr, opts) {
            if (attr.operators.indexOf(op.id) < 0 && op.appliedTypes.indexOf(attr.dataType) >= 0) {
                attr.operators.push(op.id);
            }
        });
        return op;
    };
    /**
     * Removes an operator.
     * @param id The id.
     * @param soft If `false` - removes operators from model and attributes. Otherwise only
     * from attributes.
     */
    DataModel.prototype.removeOperator = function (id, soft) {
        if (soft === void 0) { soft = false; }
        var op = dataUtils.findItemById(this.operators, id);
        if (op) {
            if (!soft)
                dataUtils.removeArrayItem(this.operators, op);
            this.runThroughEntities(function (attr, opts) {
                dataUtils.removeArrayItem(attr.operators, op.id);
            });
        }
    };
    /**
     * Get operators for data type.
     * @param type The data type
     * @return The array of operator ids.
     */
    DataModel.prototype.getOperatorIdsByDataType = function (type) {
        switch (type) {
            case DataType.String:
            case DataType.Memo:
                return 'StartsWith,EndsWith,Contains,Equal,InList,NotStartsWith,NotEndsWith,NotContains,NotEqual,NotInList,IsNull,IsNotNull'.split(',');
            case DataType.Guid:
                return 'Equal,NotEqual'.split(',');
            case DataType.Date:
            case DataType.DateTime:
                return 'DateWithinToday,DateWithinThisWeek,DateWithinPrevWeek,DateWithinThisMonth,DateWithinPrevMonth,DateWithinThisYear,DateWithinPrevYear,DateBeforePrecise,DateAfterPrecise,DatePeriodPrecise,IsNull,IsNotNull'.split(',');
            case DataType.Time:
                return 'TimeBeforePrecise,TimeAfterPrecise,TimePeriodPrecise,IsNull,IsNotNull'.split(',');
            case DataType.Byte:
            case DataType.Word:
            case DataType.Int32:
            case DataType.Int64:
            case DataType.Float:
            case DataType.Currency:
            case DataType.BCD:
            case DataType.Autoinc:
            case DataType.Unknown:
                return 'Equal,Between,LessThan,LessOrEqual,GreaterThan,GreaterOrEqual,InList,NotEqual,NotBetween,NotInList,IsNull,IsNotNull'.split(',');
            case DataType.Bool:
                return 'IsTrue,NotTrue'.split(',');
            default:
                var result = [];
                for (var _i = 0, _a = this.operators; _i < _a.length; _i++) {
                    var op = _a[_i];
                    if (op.appliedTypes.indexOf(type) >= 0)
                        result.push(op.id);
                }
                return result;
        }
    };
    /**
     * Builds entities tree used in query.
     * @param query The query.
     */
    DataModel.prototype.buildQueryPath = function (query) {
        var entity = this.mainEntity || this.rootEntity;
        var tree = this.createTreeNodeFromEntity(entity);
        tree.setParents();
        return tree;
    };
    DataModel.prototype.createTreeNodeFromEntity = function (entity) {
        var tree = new Tree(entity);
        for (var _i = 0, _a = entity.subEntities; _i < _a.length; _i++) {
            var subEnentity = _a[_i];
            tree.addChild(this.createTreeNodeFromEntity(subEnentity));
        }
        return tree;
    };
    /**
     * Get Entities used in query.
     * @param query The query.
     */
    DataModel.prototype.getEntitiesInQuery = function (query) {
        if (query.model.id !== this.id)
            throw Error('Model mithmatch');
        var entities = [];
        var columns = query.getColumns();
        for (var _i = 0, columns_1 = columns; _i < columns_1.length; _i++) {
            var column = columns_1[_i];
            if (!column.enabled)
                continue;
            this.addEntityByExpression(entities, column.expr);
        }
        var rootCond = query.getRootCondition();
        this.fillEntitiesByCondtionGroup(entities, rootCond);
        return entities;
    };
    DataModel.prototype.findAggrFunctionById = function (funcId) {
        for (var _i = 0, _a = this.aggrFunctions; _i < _a.length; _i++) {
            var func = _a[_i];
            if (func.id === funcId)
                return func;
        }
        return null;
    };
    DataModel.prototype.fillEntitiesByCondtionGroup = function (entities, root) {
        for (var _i = 0, _a = root.getConditions(); _i < _a.length; _i++) {
            var condition = _a[_i];
            if (!condition.isGroup()) {
                for (var _b = 0, _c = condition.expressions; _b < _c.length; _b++) {
                    var expr = _c[_b];
                    this.addEntityByExpression(entities, expr);
                    if (expr.args) {
                        for (var _d = 0, _e = expr.args; _d < _e.length; _d++) {
                            var arg = _e[_d];
                            this.addEntityByExpression(entities, arg);
                        }
                    }
                }
            }
            else {
                this.fillEntitiesByCondtionGroup(entities, condition);
            }
        }
    };
    DataModel.prototype.addEntityByExpression = function (entities, expr) {
        if (expr.tag !== ExprTag.EntityAttribute)
            return;
        var attrId = expr.baseAttrId || expr.value;
        if (attrId) {
            var attr = this.getAttributeById(attrId);
            if (attr) {
                if (attr.entity && dataUtils.indexOfArrayItem(entities, attr.entity) < 0) {
                    entities.push(attr.entity);
                }
            }
        }
    };
    /**
     * Finds link between two entities.
     * @param entityFrom The entity `from`.
     * @param entityTo The entity `to`.
     * @return The link.
     */
    DataModel.prototype.findLink = function (entityFrom, entityTo) {
        for (var _i = 0, _a = this.links; _i < _a.length; _i++) {
            var link = _a[_i];
            if (link.entityFrom == entityFrom
                && link.entityTo == entityTo)
                return link;
        }
        return null;
    };
    /**
     * Get links with the entity.
     * @param entity The entity.
     * @return The link.
     */
    DataModel.prototype.getLinksByEntity = function (entity) {
        var result = [];
        for (var _i = 0, _a = this.links; _i < _a.length; _i++) {
            var link = _a[_i];
            if (link.entityFrom == entity || link.entityTo == entity) {
                result.push(link);
            }
        }
        return result;
    };
    /**
     * Gets entities tree.
     * @param opts The options.
     * @param filterFunc The filter function.
     * Takes two parameters, Entity and EntityAttr (second parameter will be null for entities), and returns boolean (true if the corresponding entity or attribute).
     * @returns The tree of the entities and their attributes according to options and the filter function
     */
    DataModel.prototype.getEntitiesTree = function (opts, filterFunc) {
        return this._listByEntity(this.getRootEntity(), opts, filterFunc);
    };
    /**
     * Gets entities tree due to filter.
     * @param filterFunc The filter function.
     * Takes two parameters, Entity and EntityAttr (second parameter will be null for entities), and returns boolean (true if the corresponding entity or attribute).
     * @returns The tree of the entities and their attributes according to the filter function
     */
    DataModel.prototype.getEntitiesTreeWithFilter = function (filterFunc) {
        return this._listByEntityWithFilter(this.getRootEntity(), filterFunc);
    };
    /**
     * Finds full entity path by attribute
     * @param attrId The attribute id.
     * @param sep The separator.
     * @returns The path.
     */
    DataModel.prototype.getFullEntityPathByAttr = function (attrId, sep) {
        sep = sep || ' ';
        return this.getEntityPathByAttr(this.getRootEntity(), attrId, sep, true);
    };
    /**
    * Finds entity path by attribute
    * @param entity The entity.
    * @param attrId The attribute id.
    * @param sep The separator.
    * @param root The root option.
    * @returns The path.
    */
    DataModel.prototype.getEntityPathByAttr = function (entity, attrId, sep, root) {
        return _super.prototype.getEntityPathByAttr.call(this, entity, attrId, sep, root);
    };
    /**
     * Gets the attribute text.
     * @param attr The attribute.
     * @param format The format.
     * @returns Formatted text.
     */
    DataModel.prototype.getAttributeText = function (attr, format) {
        return _super.prototype.getAttributeText.call(this, attr, format);
    };
    DataModel.prototype.getFirstUICAttr = function () {
        return this.getFirstUICAttrInEntity(this.getRootEntity());
    };
    /**
     * Gets first `UIC` attribute in specified entity
     * (`UIC` stands for `use in conditions` - so such attribute can be used in conditions)
     * @param entity Entity object to search our attribute in.
     * @returns The entity attribute or `null`
     */
    DataModel.prototype.getFirstUICAttrInEntity = function (entity) {
        if (entity.useInConditions !== false) {
            if (entity.attributes) {
                var attrCount = entity.attributes.length;
                for (var i = 0; i < attrCount; i++) {
                    if (entity.attributes[i].useInConditions) {
                        return entity.attributes[i];
                    }
                }
            }
            if (entity.subEntities) {
                var subEntityCount = entity.subEntities.length;
                for (var i = 0; i < subEntityCount; i++) {
                    var result = this.getFirstUICAttrInEntity(entity.subEntities[i]);
                    if (result) {
                        return result;
                    }
                }
            }
        }
        return null;
    };
    /**
     * Scans model's entity tree and calls the callback functions for each attribute and entity.
     * @param processAttribute The callback function which is called for each attribute in model's entity tree.
     * The processed attribute is passed in the first function parameter.
     * @param processEntity The callback function which is called for each entity in tree.
     * The processed entity is passed in the first function parameter.
     */
    DataModel.prototype.runThroughEntities = function (processAttribute, processEntity) {
        this.getRootEntity().scan(processAttribute, processEntity);
    };
    /**
     * Finds first attribute by filter.
     * @param filterFunc The filter function. Takes EntityAttr object in parameter and returns boolean
     */
    DataModel.prototype.getFirstAttributeByFilter = function (filterFunc) {
        var res = null;
        this.runThroughEntities(function (attr, opts) {
            if (filterFunc(attr)) {
                opts.stop = true;
                res = attr;
            }
        }, null);
        return res;
    };
    /**
     * Finds operator in model by its ID.
     * This function runs through all operators inside specified model and returns the one with specified ID.
     * @param operatorId An operator ID.
     * @returns The operator or `null`.
     */
    DataModel.prototype.findOperatorById = function (operatorId) {
        if (this.operators.length > 0) {
            var opCount = this.operators.length;
            for (var idx = 0; idx < opCount; idx++) {
                if (this.operators[idx].id == operatorId) {
                    return this.operators[idx];
                }
            }
        }
        return null;
    };
    /**
     * Finds operator in model by its ID.
     * his function runs through all operators inside specified model and returns the one with specified ID.
     * @param operatorId Operator ID.
     * @returns The operator or special `NullOperator` object if operator is not found.
     */
    DataModel.prototype.getOperatorById = function (operatorId) {
        var op = this.findOperatorById(operatorId);
        if (!op)
            return this.nullOperator;
        return op;
    };
    DataModel.prototype.getDefaultOperatorIdForAttr = function (attr) {
        if (attr.defaultOperator) {
            return attr.defaultOperator;
        }
        else if (attr.operators.length > 0) {
            return attr.operators[0];
        }
        else {
            return this.nullOperator.id;
        }
    };
    /**
     * Gets caption of the aggregate function.
     * @param funcId The function id.
     * @returns The caption.
     */
    DataModel.prototype.getAggrFunctionCaption = function (funcId) {
        var funcTexts = i18n.getText('AggregateFunctions', funcId);
        var funcCaption = funcTexts ? funcTexts.caption : i18n.getText('Aggr' + funcId.replace(' ', '') + '_Caption');
        if (funcCaption) {
            return funcCaption;
        }
        var func = dataUtils.findItemById(this.aggrFunctions, funcId);
        if (!func || !func.caption)
            return funcId;
        return func.caption;
    };
    /**
     * Gets format of the aggregate function.
     * @param funcId The function id.
     * @returns The caption.
     */
    DataModel.prototype.getAggrFunctionFormat = function (funcId) {
        var funcTexts = i18n.getText('AggregateFunctions', funcId);
        var funcFormat = funcTexts ? funcTexts.displayFormat : i18n.getText('Aggr' + funcId.replace(' ', '') + '_Format');
        if (funcFormat) {
            return funcFormat;
        }
        var func = dataUtils.findItemById(this.aggrFunctions, funcId);
        if (!func || !func.displayFormat)
            return '';
        return func.displayFormat;
    };
    /**
     * Gets default operator for an attribute.
     * @param attr The attribute.
     * @returns The operator.
     */
    DataModel.prototype.getDefaultOperatorForAttr = function (attr) {
        var operatorId = this.getDefaultOperatorIdForAttr(attr);
        return this.getOperatorById(operatorId);
    };
    /**
     * Gets operand.
     * @param attr The attribute.
     * @param operator The operator.
     * @param index The index.
     * @returns The operand.
     */
    DataModel.prototype.getOperand = function (attr, operator, index) {
        var defOperand = new Operand();
        if (operator && operator.defaultOperand) {
            defOperand.copyFrom(operator.defaultOperand);
            if (!defOperand.defValue) {
                defOperand.defValue = '';
            }
            if (!defOperand.defText) {
                defOperand.defText = '';
            }
        }
        else {
            defOperand.kind = DataKind.Scalar;
            defOperand.dataType = attr.dataType;
            defOperand.defValue = '';
            defOperand.defText = '';
            defOperand.editor = null;
        }
        var result = defOperand;
        if (result.dataType === DataType.Unknown && attr) {
            result.dataType = attr.dataType;
        }
        if (operator && (index >= 0)) {
            if (operator.operands && operator.operands[index - 1]) {
                result = dataUtils.assign(result, operator.operands[index - 1]);
            }
        }
        ;
        if (!result.editor) {
            if (defOperand.editor) {
                result.editor = defOperand.editor;
            }
            else if (defOperand.kind === DataKind.Query) {
                result.editor.tag = EqEditorTag.SubQuery;
            }
            else if (attr && attr.defaultEditor) {
                result.editor = attr.defaultEditor;
            }
            else if (result.dataType === DataType.Date || result.dataType === DataType.DateTime
                || result.dataType === DataType.Time) {
                result.editor.tag = EqEditorTag.DateTime;
            }
            else {
                result.editor.tag = EqEditorTag.Edit;
            }
        }
        return result;
    };
    /**
     * Gets the list of aggegate functions.
     * @returns AN array of aggregate functions.
     */
    DataModel.prototype.getAggrFunctions = function () {
        return this.aggrFunctions;
    };
    /**
     * Checks wether macro value is date.
     * @param macroExpr The macro value.
     * @retruns `true` if the macro value is date, otherwise `false`.
     */
    DataModel.prototype.isDateMacro = function (macroExpr, macroProcessor) {
        var match = macroExpr.match(/\${{?(.*?)}?}/);
        if (match != null) {
            var macroId = match[1];
            if (dataUtils.indexOfArrayItem(this.dateMacros, macroId) >= 0) {
                if (macroProcessor) {
                    macroProcessor(macroId);
                }
                return true;
            }
        }
        return false;
    };
    /**
     * Checks whether the expression passed in the parameter is an time macro.
     * @param macroExpr The macro expression
     * @retruns `true` if the expression is a time macro (like Now), otherwise `false`.
     */
    DataModel.prototype.isTimeMacro = function (macroExpr, macroProcessor) {
        var match = macroExpr.match(/\${{?(.*?)}?}/);
        if (match != null) {
            var macroId = match[1];
            if (dataUtils.indexOfArrayItem(this.timeMacros, macroId) >= 0) {
                if (macroProcessor) {
                    macroProcessor(macroId);
                }
                return true;
            }
        }
        return false;
    };
    /**
     * Gets date by its macro value.
     * @param macroExpr The macro value.
     * @returns The date or `null`
     */
    DataModel.prototype.getDateMacroValue = function (macroExpr) {
        var d = new Date();
        var res = this.isDateMacro(macroExpr, function (macroId) {
            switch (macroId) {
                case 'Today':
                    break;
                case 'Yesterday':
                    d.setDate(d.getDate() - 1);
                    break;
                case 'Tomorrow':
                    d.setDate(d.getDate() + 1);
                    break;
                case 'FirstDayOfMonth':
                    d.setDate(1);
                    break;
                case 'LastDayOfMonth':
                    d.setMonth(d.getMonth() + 1, 0);
                    break;
                case 'FirstDayOfPrevWeek':
                    var day = d.getDay();
                    day = (day == 0) ? 1 : 8 - day; //We start week from Monday, but js - from Sunday
                    d.setDate(d.getDate() - day);
                    break;
                case 'FirstDayOfWeek':
                    var day = d.getDay();
                    day = (day == 0) ? 6 : day - 1; //We start week from Monday, but js - from Sunday
                    d.setDate(d.getDate() - day);
                    break;
                case 'FirstDayOfYear':
                    d.setMonth(0, 1);
                    break;
                case 'FirstDayOfNextWeek':
                    var day = d.getDay();
                    day = (day == 0) ? 1 : 8 - day; //We start week from Monday, but js - from Sunday
                    d.setDate(d.getDate() + day);
                    break;
                case 'FirstDayOfNextMonth':
                    d.setMonth(d.getMonth() + 1, 1);
                    break;
                case 'FirstDayOfPrevMonth':
                    d.setMonth(d.getMonth() - 1, 1);
                    break;
                case 'FirstDayOfPrevYear':
                    d.setFullYear(d.getFullYear() - 1, 0, 1);
                    break;
                case 'FirstDayOfNextYear':
                    d.setFullYear(d.getFullYear() + 1, 0, 1);
                    break;
            }
            ;
        });
        return res ? d : null;
    };
    /**
    * Gets the date value by a macro expression of the expression itself.
    * @param macroExpr The macro value.
    * @returns The date or macro valu.
    */
    DataModel.prototype.getDateOrMacroValue = function (macroExpr) {
        var d = this.getDateMacroValue(macroExpr);
        return d ? d : macroExpr;
    };
    /**
    * Gets time by its macro value.
    * @param macroExpr The macro value.
    * @returns The date or `null`.
    */
    DataModel.prototype.getTimeMacroValue = function (macroExpr) {
        var d = new Date();
        var res = this.isDateMacro(macroExpr, function (macroId) {
            switch (macroId) {
                case 'Now':
                    break;
                case 'HourStart':
                    d.setMinutes(0, 0, 0);
                    break;
                case 'Midnight':
                    d.setHours(0, 0, 0, 0);
                    break;
                case 'Noon':
                    d.setHours(12, 0, 0, 0);
                    break;
            }
            ;
        });
        return res ? d : null;
    };
    /**
     * Gets time or macro by macro value.
     * @param macroValue The macro value.
     * @returns The time or macro valu.
     */
    DataModel.prototype.getTimeOrMacroValue = function (time) {
        var t = this.getTimeMacroValue(time);
        return t ? t : time;
    };
    // /**
    //  * Gets all date marcos.
    //  * @returns The array of marcos
    //  */
    DataModel.prototype.getAllDateMacros = function () {
        return this.dateMacros;
    };
    /**
     * Gets all time marcos.
     * @returns The array of marcos
     */
    DataModel.prototype.getAllTimeMacros = function () {
        return this.timeMacros;
    };
    return DataModel;
}(MetaData));
export { DataModel };
//# sourceMappingURL=data_model.js.map